import * as Core from "@zeller-public/payments-sdk-core";

import { logger } from "../shared/logger";

////////////////////////////////////////////////////////////////////////////////

const MAX_HANDLER_ATTEMPTS = 3;

// cross-platform post message
// we're expecting the wrapping webview/iframe to handle our message but
// have no knowledge of its state so wrapping in try/catch to handle any thrown
// errors to prevent the spa crashing
export function $postMessage(message: string): void {
	const isOk = message === Core.Protocol.OK;
	let lastException: Error | undefined = undefined;

	function attemptPost(attempt: number) {
		// only post to the first matching handler as we only handle one
		const handler = handlers.find((handler) => handler.isPresent());

		if (handler) {
			try {
				handler.post(message);

				// raise sentry message if we successfully handled a
				// thrown exception on a reattempt
				if (lastException && !isOk) {
					logger.info("post message: handled thrown exception on reattempt", {
						attempt,
						exception: lastException,
						handler: handler.name,
					});
				}

				lastException = undefined;

				return;
			} catch (error) {
				if (error instanceof Error && !isOk) {
					logger.error("post message: handle caught error", {
						attempt: attempt + 1,
						handler: handler.name,
					});

					lastException = error;
				}
			}
		}

		if (attempt < MAX_HANDLER_ATTEMPTS) {
			setTimeout(() => attemptPost(attempt + 1), 100);
		} else if (!isOk) {
			const message = lastException
				? "post message: handler found but failed to post after max attempts"
				: "post message: missed all handlers after max attempts";

			logger.warn(message);
		}
	}

	attemptPost(0);
}

////////////////////////////////////////////////////////////////////////////////

type Handler = {
	name: string;
	isPresent: () => boolean;
	post: (message: string) => void;
};

const handlers: Handler[] = [
	// c++ desktop applications
	{
		name: "window.handler",
		isPresent: () => Boolean(window.handler?.postMessage),
		post: (message: string) => window.handler!.postMessage(message),
	},
	// react native webview
	{
		name: "window.ReactNativeWebView",
		isPresent: () => Boolean(window.ReactNativeWebView?.postMessage),
		post: (message: string) => window.ReactNativeWebView!.postMessage(message),
	},

	// ios webview
	{
		name: "window.webkit",
		isPresent: () =>
			Boolean(window.webkit?.messageHandlers?.handler?.postMessage),
		post: (message: string) =>
			window.webkit!.messageHandlers.handler.postMessage(message),
	},

	// microsoft webview
	{
		name: "window.chrome",
		isPresent: () => Boolean(window.chrome?.webview?.postMessage),
		post: (message: string) => window.chrome!.webview.postMessage(message),
	},

	// iframe
	{
		name: "window.parent",
		isPresent: () => Boolean(window.parent !== window),
		post: (message: string) => window.parent.postMessage(message, "*"),
	},
];
